import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';

import { InvoiceModel, LineModel } from '@data-layer/models/entry-summaries';
import { LINE_ITEMS_BULK_DELETE_LIMIT } from '@data-layer/repositories/line-items.repository';
import { EntrySummaryValidationHelper } from '@helpers/entry-summary-validation.helper';
import { IncoTermModel } from '@data-layer/models/common';
import { enumDropdownActionIconColor, enumDropdownActionMenuMode } from '@shared/form-wrapper/enums';
import { CURRENCY_IS_NOT_AVAILABLE } from '@constants/entity-validation-messages';
import { CustomDateByUtcPipe } from '@shared/pipes-shared/pipes';
import { DEFAULT_CURRENCY_CODE } from '@constants/currency';
import { DateHelper } from '@helpers/date.helper';
import { enumTransportationMode } from '@data-layer/enums/entry-summaries';
import { EntityValidationHelper } from '@helpers/entity-validation.helper';
import { AlertService } from '@services/alert.service';
import { CompaniesService } from '@services/companies.service';
import { enumUserBetaFlag } from '@data-layer/enums/user';
import { EntrySummaryStateService } from '@shared/es-mutual/services';
import { flatten } from 'lodash-es';
import { take } from 'rxjs/operators';

import { MasterCheckboxState } from '../es-line-items-bulk-delete-modal/es-line-items-bulk-delete-modal.component';

@Component({
  selector: 'es-invoice-summary-details',
  templateUrl: './es-invoice-summary-details.component.html',
  styleUrls: ['./es-invoice-summary-details.component.scss'],
})
export class EsInvoiceSummaryDetailsComponent implements OnInit, OnChanges {
  @Input() public transportationMode: enumTransportationMode;
  // #13499 — 1-based ordinal inside the BL; parents bind it only when the BL has 2+ invoices, null → no "#N" in the header
  @Input() public index: number = null;
  @Input() public invoice: InvoiceModel;
  @Input() public isCanEdit: boolean = false;
  @Input() public currencyRate: number = 1;
  @Input() public exportDate: string = DateHelper.now;
  @Input() public incoterms: IncoTermModel[] = [];
  @Input() public isCanView: boolean = true;
  // Client-only gate for the line items multi-select / bulk-delete UI. Unbound → false.
  @Input() public isCanBulkDelete: boolean = false;
  // #13410 "Finalize" (Validate HTS codes) in the mobile invoice card menu; unbound → hidden, hidden once every line is verified.
  @Input() public isShowFinalize: boolean;
  @Input() public isFinalizeEnabled: boolean;

  @Output() public edit: EventEmitter<number> = new EventEmitter<number>();
  @Output() public delete: EventEmitter<number> = new EventEmitter<number>();
  @Output() public editItem: EventEmitter<number> = new EventEmitter<number>();
  @Output() public deleteItem: EventEmitter<number> = new EventEmitter<number>();
  @Output() public duplicateItem: EventEmitter<LineModel> = new EventEmitter<LineModel>();
  @Output() public addNewItem: EventEmitter<number> = new EventEmitter<number>();
  @Output() public linesDeleted: EventEmitter<void> = new EventEmitter<void>();
  @Output() public finalize: EventEmitter<number> = new EventEmitter<number>();

  public iconColor: enumDropdownActionIconColor = enumDropdownActionIconColor.WHITE;
  public menuMode: enumDropdownActionMenuMode = enumDropdownActionMenuMode.BLUE;
  public open: boolean = false;

  public isMultiSelectMode: boolean = false;
  public selectedIds: Set<number> = new Set<number>();
  public isBulkDeleteModalOpen: boolean = false;
  public isBulkDeleting: boolean = false;
  public fieldsConfig: {
    [key: string]: {
      value: any;
      messages: string[];
      firstError: string;
      hasError: boolean;
      hasValue: boolean;
      shown: boolean;
      params?: any;
    };
  } = {};

  public manufacturerFieldsConfig: {
    [key: string]: {
      value: any;
      messages: string[];
      firstError: string;
      hasError: boolean;
      hasValue: boolean;
      shown: boolean;
    };
  } = {};

  private pendingDeleteIds: number[] = [];

  constructor(
    private customDatePipe: CustomDateByUtcPipe,
    private entrySummaryStateService: EntrySummaryStateService,
    private alertService: AlertService,
    private companiesService: CompaniesService,
  ) {}

  public ngOnInit(): void {
    this.open = !!JSON.parse(localStorage.getItem('ES_INVOICE_SUMMARY_IS_OPEN' + this.invoice.id));
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.invoice && this.invoice) {
      this.getFieldsConfig();
      if (!this.open) {
        this.open =
          flatten(this.invoice.lines.map((line) => EntrySummaryValidationHelper.getValidationMessages(line, this.transportationMode)))
            .length > 0;
      }

      this.syncSelectionWithLines(changes);
    }
  }

  public toggleOpen() {
    this.open = !this.open;
    localStorage.setItem('ES_INVOICE_SUMMARY_IS_OPEN' + this.invoice.id, JSON.stringify(this.open));
  }

  public onEdit() {
    this.edit.emit(this.invoice.id);
  }

  public onDelete() {
    this.delete.emit(this.invoice.id);
  }

  public onEditItem(itemId: number) {
    this.editItem.emit(itemId);
  }

  public onDeleteItem(itemId: number) {
    this.deleteItem.emit(itemId);
  }

  public onDuplicateItem(item: LineModel) {
    this.duplicateItem.emit(item);
  }

  /** Hidden once no line of the Bill of Lading needs classification; disabled on an entry the user cannot edit. */
  public get isShowFinalizeButton(): boolean {
    return (
      this.isShowFinalize === true &&
      this.isFinalizeEnabled === true &&
      this.companiesService.hasUserBetaFlag(enumUserBetaFlag.htsClassificationAssistant)
    );
  }

  public get isFinalizeDisabled(): boolean {
    return !this.isCanEdit;
  }

  public onFinalize(): void {
    this.finalize.emit(this.invoice.id);
  }

  public onAddNewItem() {
    this.addNewItem.emit(this.invoice.id);
  }

  public get indexSuffix(): string {
    return this.index > 0 ? ' #' + this.index : '';
  }

  public get masterState(): MasterCheckboxState {
    const total = this.invoice?.lines?.length || 0;
    const selected = this.selectedIds.size;

    if (!selected) {
      return 'unchecked';
    }

    return selected === total ? 'checked' : 'indeterminate';
  }

  public enterMultiSelect(): void {
    this.isMultiSelectMode = true;
    this.selectedIds = new Set<number>();
    // Bulk selection needs the line items visible; not via toggleOpen() so the user's
    // persisted collapse preference (localStorage) is left untouched.
    this.open = true;
  }

  public exitMultiSelect(): void {
    this.isMultiSelectMode = false;
    this.selectedIds = new Set<number>();
  }

  public toggleRow(id: number): void {
    const next = new Set(this.selectedIds);
    if (next.has(id)) {
      next.delete(id);
    } else {
      next.add(id);
    }
    this.selectedIds = next;
  }

  public onMasterClick(): void {
    if (this.masterState === 'checked') {
      this.selectedIds = new Set<number>();
      return;
    }

    this.selectAll();
  }

  public selectAll(): void {
    this.selectedIds = new Set((this.invoice?.lines || []).map((line) => line.id));
  }

  public selectNone(): void {
    this.selectedIds = new Set<number>();
  }

  public openBulkDeleteModal(): void {
    if (!this.selectedIds.size) {
      return;
    }

    this.openDeleteModal((line) => this.selectedIds.has(line.id));
  }

  public openRemoveAllModal(): void {
    this.openDeleteModal(() => true);
  }

  public onBulkDeleteConfirmed(): void {
    if (this.isBulkDeleting) {
      return;
    }

    this.isBulkDeleting = true;
    this.entrySummaryStateService
      .bulkDeleteLineItems(this.pendingDeleteIds)
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.isBulkDeleting = false;
          this.isBulkDeleteModalOpen = false;
          this.exitMultiSelect();
          this.linesDeleted.emit();
        },
        error: () => {
          // The global error alert is shown by ApiService/ApiErrorService; selection is kept.
          this.isBulkDeleting = false;
          this.isBulkDeleteModalOpen = false;
        },
      });
  }

  public onBulkDeleteCancelled(): void {
    this.isBulkDeleteModalOpen = false;
  }

  public get incotermName() {
    const code = this.invoice.incoTermsCode;
    const incoterm = (this.incoterms || []).find((item) => item.code === code);
    return incoterm ? incoterm.code + ' ' + incoterm.name : code;
  }

  private syncSelectionWithLines(changes: SimpleChanges): void {
    if (changes.invoice.isFirstChange()) {
      return;
    }

    const lines = this.invoice.lines;
    if (!lines?.length) {
      this.exitMultiSelect();
      return;
    }

    const actualIds = new Set(lines.map((line) => line.id));
    this.selectedIds = new Set([...this.selectedIds].filter((id) => actualIds.has(id)));
  }

  private openDeleteModal(isIncluded: (line: LineModel) => boolean): void {
    const ids = (this.invoice?.lines || []).filter((line) => isIncluded(line)).map((line) => line.id);

    if (ids.length > LINE_ITEMS_BULK_DELETE_LIMIT) {
      this.alertService.warning('BULK_DELETE_LINE_ITEMS_LIMIT', 'BULK_DELETE_LINE_ITEMS_TITLE', { limit: LINE_ITEMS_BULK_DELETE_LIMIT });
      return;
    }

    this.pendingDeleteIds = ids;
    this.isBulkDeleteModalOpen = true;
  }

  private getFieldsConfig() {
    this.fieldsConfig = {};
    Object.keys(this.invoice).forEach((fieldName: string) => {
      const value = this.invoice[fieldName];
      const messages = EntrySummaryValidationHelper.invoiceMapValidation.has(fieldName)
        ? EntrySummaryValidationHelper.invoiceMapValidation.get(fieldName)(this.invoice)
        : [];
      this.fieldsConfig[fieldName] = {
        value,
        messages,
        firstError: messages && messages.length ? 'BACK_ERROR.' + messages[0] : null,
        hasError: !!(messages && messages.length),
        hasValue: !!value,
        shown: !!(messages && messages.length) || !!value,
      };
    });

    this.fieldsConfig.currencyRate = {
      value: this.currencyRate,
      messages: isNaN(this.currencyRate) ? [CURRENCY_IS_NOT_AVAILABLE] : [],
      firstError: isNaN(this.currencyRate) ? 'FORM_ERROR.' + CURRENCY_IS_NOT_AVAILABLE : null,
      hasError: isNaN(this.currencyRate),
      hasValue: !!this.currencyRate,
      shown: this.invoice.currency !== DEFAULT_CURRENCY_CODE,
      params: { exportDate: this.customDatePipe.transform(this.exportDate), currency: this.invoice.currency },
    };

    this.manufacturerFieldsConfig = {};
    Object.keys(this.invoice.manufacturer).forEach((fieldName: string) => {
      const value = this.invoice[fieldName];
      const messages = EntityValidationHelper.entityMapValidation.has(fieldName)
        ? EntityValidationHelper.entityMapValidation.get(fieldName)(this.invoice.manufacturer)
        : [];
      this.manufacturerFieldsConfig[fieldName] = {
        value,
        messages,
        firstError: messages && messages.length ? 'BACK_ERROR.' + messages[0] : null,
        hasError: !!(messages && messages.length),
        hasValue: !!value,
        shown: !!(messages && messages.length) || !!value,
      };
    });
  }
}
